/**
 * AOI API Service
 * Handles all API communication with the backend
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

/**
 * Generic fetch wrapper with error handling
 */
async function fetchAPI(endpoint, options = {}) {
  const url = `${API_BASE_URL}${endpoint}`;

  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  const response = await fetch(url, { ...defaultOptions, ...options });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP error! status: ${response.status}`);
  }

  return response.json();
}

// ==================== Health Check ====================

export async function healthCheck() {
  return fetchAPI('/health');
}

// ==================== Entity Operations ====================

export async function getEntities(params = {}) {
  const queryParams = new URLSearchParams();
  if (params.type) queryParams.append('type', params.type);
  // Default to 2000 to get all entities for graph visualization
  queryParams.append('limit', params.limit || 2000);

  const queryString = queryParams.toString();
  return fetchAPI(`/entities${queryString ? `?${queryString}` : ''}`);
}

export async function getEntity(entityId) {
  return fetchAPI(`/entities/${entityId}`);
}

export async function getEntityRelationships(entityId, params = {}) {
  const queryParams = new URLSearchParams();
  if (params.type) queryParams.append('type', params.type);
  if (params.direction) queryParams.append('direction', params.direction);

  const queryString = queryParams.toString();
  return fetchAPI(`/entities/${entityId}/relationships${queryString ? `?${queryString}` : ''}`);
}

export async function createEntity(entityData) {
  return fetchAPI('/ingest/entity', {
    method: 'POST',
    body: JSON.stringify(entityData),
  });
}

export async function updateEntity(entityId, updates) {
  return fetchAPI(`/entities/${entityId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

export async function deleteEntity(entityId) {
  return fetchAPI(`/entities/${entityId}`, {
    method: 'DELETE',
  });
}

// ==================== Relationship Operations ====================

export async function createRelationship(relationshipData) {
  return fetchAPI('/ingest/relationship', {
    method: 'POST',
    body: JSON.stringify(relationshipData),
  });
}

// ==================== Query Operations ====================

export async function queryEntities() {
  return fetchAPI('/query/entities');
}

export async function graphQuery(params) {
  return fetchAPI('/query/graph', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

export async function semanticSearch(query, options = {}) {
  return fetchAPI('/query/semantic', {
    method: 'POST',
    body: JSON.stringify({
      query,
      top_k: options.topK || 10,
      threshold: options.threshold || 0.7,
      entity_types: options.entityTypes || [],
    }),
  });
}

// ==================== Extraction Operations ====================

export async function extractFromText(content, autoCreate = false) {
  return fetchAPI('/extract/text', {
    method: 'POST',
    body: JSON.stringify({ content, auto_create: autoCreate }),
  });
}

export async function extractFromUrl(url, autoCreate = false) {
  return fetchAPI('/extract/url', {
    method: 'POST',
    body: JSON.stringify({ url, auto_create: autoCreate }),
  });
}

export async function extractFromFile(content, filename, autoCreate = false) {
  return fetchAPI('/extract/file', {
    method: 'POST',
    body: JSON.stringify({
      content: btoa(content),
      filename,
      auto_create: autoCreate,
    }),
  });
}

// ==================== Insight Operations ====================

export async function submitInsight(content, autoCreate = true) {
  return fetchAPI('/ingest/insight', {
    method: 'POST',
    body: JSON.stringify({ content, auto_create: autoCreate }),
  });
}

// ==================== Bulk Import Operations ====================

export async function initiateBulkUpload(files) {
  return fetchAPI('/bulk/upload', {
    method: 'POST',
    body: JSON.stringify({ files }),
  });
}

export async function uploadBulkInlineData(inlineData) {
  return fetchAPI('/bulk/upload', {
    method: 'POST',
    body: JSON.stringify({ inline_data: inlineData }),
  });
}

export async function processBulkJob(jobId) {
  return fetchAPI('/bulk/process', {
    method: 'POST',
    body: JSON.stringify({ job_id: jobId }),
  });
}

export async function getBulkJobStatus(jobId) {
  return fetchAPI(`/bulk/status/${jobId}`);
}

// ==================== Statistics ====================

export async function getStatistics() {
  return fetchAPI('/stats');
}

// ==================== Relationships ====================

export async function getRelationships(params = {}) {
  const queryParams = new URLSearchParams();
  // Default to 2000 to get all relationships for graph visualization
  queryParams.append('limit', params.limit || 2000);

  const queryString = queryParams.toString();
  return fetchAPI(`/relationships${queryString ? `?${queryString}` : ''}`);
}

// ==================== Ask Atlas (RAG Chat) ====================

export async function askAtlas(question) {
  return fetchAPI('/ask', {
    method: 'POST',
    body: JSON.stringify({ question }),
  });
}

// ==================== Mock Data for Development ====================

const MOCK_ENTITIES = [
  { id: 'svc-payment', name: 'payment-service', entity_type: 'SERVICE', description: 'Core payment processing for ACH, wire transfers, and card transactions.' },
  { id: 'svc-auth', name: 'auth-service', entity_type: 'SERVICE', description: 'Central authentication and authorization service.' },
  { id: 'svc-loans', name: 'loans-service', entity_type: 'SERVICE', description: 'Auto loan origination and servicing.' },
  { id: 'svc-accounts', name: 'accounts-service', entity_type: 'SERVICE', description: 'Customer account management.' },
  { id: 'svc-fraud', name: 'fraud-detection-service', entity_type: 'SERVICE', description: 'Real-time fraud detection using ML models.' },
  { id: 'team-retail', name: 'Retail Banking', entity_type: 'TEAM', description: 'Responsible for consumer banking products.' },
  { id: 'team-platform', name: 'Platform Engineering', entity_type: 'TEAM', description: 'Core platform infrastructure.' },
  { id: 'pattern-oauth2', name: 'OAuth 2.0 + PKCE', entity_type: 'PATTERN', description: 'Standard OAuth 2.0 authorization code flow.' },
  { id: 'pattern-circuit', name: 'Circuit Breaker', entity_type: 'PATTERN', description: 'Prevents cascade failures.' },
  { id: 'reg-pci', name: 'PCI-DSS', entity_type: 'REGULATION', description: 'Payment Card Industry Data Security Standard.' },
];

const MOCK_RELATIONSHIPS = [
  { source_id: 'svc-payment', target_id: 'svc-auth', rel_type: 'DEPENDS_ON', description: 'Authentication for payment authorization' },
  { source_id: 'svc-payment', target_id: 'svc-fraud', rel_type: 'DEPENDS_ON', description: 'Real-time fraud check' },
  { source_id: 'svc-payment', target_id: 'svc-accounts', rel_type: 'DEPENDS_ON', description: 'Balance verification' },
  { source_id: 'svc-loans', target_id: 'svc-auth', rel_type: 'DEPENDS_ON', description: 'User authentication' },
  { source_id: 'svc-loans', target_id: 'svc-payment', rel_type: 'DEPENDS_ON', description: 'Loan disbursement' },
  { source_id: 'svc-payment', target_id: 'team-retail', rel_type: 'OWNED_BY', description: 'Primary ownership' },
  { source_id: 'svc-auth', target_id: 'team-platform', rel_type: 'OWNED_BY', description: 'Primary ownership' },
  { source_id: 'svc-auth', target_id: 'pattern-oauth2', rel_type: 'USES_PATTERN', description: 'OAuth implementation' },
  { source_id: 'svc-payment', target_id: 'pattern-circuit', rel_type: 'USES_PATTERN', description: 'External service protection' },
  { source_id: 'svc-payment', target_id: 'reg-pci', rel_type: 'COMPLIES_WITH', description: 'Card data handling' },
];

// Use mock data if API is not available
let useMockData = false;

export function enableMockData() {
  useMockData = true;
}

export function disableMockData() {
  useMockData = false;
}

// Mock implementations
export async function getMockEntities() {
  return { entities: MOCK_ENTITIES, total: MOCK_ENTITIES.length };
}

export async function getMockRelationships() {
  return MOCK_RELATIONSHIPS;
}

export async function getMockStatistics() {
  return {
    statistics: {
      service: 5,
      team: 2,
      pattern: 2,
      regulation: 1,
      relationships: 10,
    }
  };
}
